/**
 * useExamApplications Hook
 * Custom hook for managing exam application state
 *
 * @version 2025.1.0
 */

import { useState, useCallback } from 'react';
import { examApplicationService } from '../../../services/examApplicationService';
import {
  ApplicationLevel,
  ApplicationData,
  ExamWithApplication,
  AvailableExam,
  ExpirationAlerts,
} from '../../../types/examApplication';

// ============================================================================
// TYPES
// ============================================================================

interface UseExamApplicationsState {
  applied: ExamWithApplication[];
  available: AvailableExam[];
  alerts: ExpirationAlerts | null;
  loading: boolean;
  loadingAlerts: boolean;
  error: string | null;
  alertsError: string | null;
}

interface UseExamApplicationsReturn extends UseExamApplicationsState {
  loadExams: (level: ApplicationLevel, targetId: number) => Promise<void>;
  loadAlerts: (companyId: number, daysAhead?: number) => Promise<void>;
  applyExam: (
    level: ApplicationLevel,
    targetId: number,
    examId: number,
    data?: ApplicationData
  ) => Promise<boolean>;
  applyMultipleExams: (
    level: ApplicationLevel,
    targetId: number,
    examIds: number[],
    data?: ApplicationData
  ) => Promise<boolean>;
  removeExam: (
    level: ApplicationLevel,
    targetId: number,
    examId: number
  ) => Promise<boolean>;
  removeMultipleExams: (
    level: ApplicationLevel,
    targetId: number,
    examIds: number[]
  ) => Promise<boolean>;
  updateApplication: (
    level: ApplicationLevel,
    targetId: number,
    examId: number,
    data: ApplicationData
  ) => Promise<boolean>;
  clearError: () => void;
  clearAlertsError: () => void;
}

// ============================================================================
// HOOK
// ============================================================================

export function useExamApplications(): UseExamApplicationsReturn {
  const [state, setState] = useState<UseExamApplicationsState>({
    applied: [],
    available: [],
    alerts: null,
    loading: false,
    loadingAlerts: false,
    error: null,
    alertsError: null,
  });

  /**
   * Load exams for a target
   */
  const loadExams = useCallback(
    async (level: ApplicationLevel, targetId: number): Promise<void> => {
      setState((prev) => ({ ...prev, loading: true, error: null }));

      try {
        const response = await examApplicationService.getForTarget(level, targetId);
        setState((prev) => ({
          ...prev,
          applied: response.applied,
          available: response.available,
          loading: false,
        }));
      } catch (err: unknown) {
        const error = err as { message?: string };
        setState((prev) => ({
          ...prev,
          loading: false,
          error: error.message ?? 'Erro ao carregar exames',
        }));
      }
    },
    []
  );

  /**
   * Load expiration alerts for a company
   */
  const loadAlerts = useCallback(
    async (companyId: number, daysAhead?: number): Promise<void> => {
      setState((prev) => ({ ...prev, loadingAlerts: true, alertsError: null }));

      try {
        const response = await examApplicationService.getAlertsForCompany(
          companyId,
          daysAhead
        );
        setState((prev) => ({
          ...prev,
          alerts: response,
          loadingAlerts: false,
        }));
      } catch (err: unknown) {
        const error = err as { message?: string };
        setState((prev) => ({
          ...prev,
          loadingAlerts: false,
          alertsError: error.message ?? 'Erro ao carregar alertas',
        }));
      }
    },
    []
  );

  /**
   * Apply a single exam
   */
  const applyExam = useCallback(
    async (
      level: ApplicationLevel,
      targetId: number,
      examId: number,
      data?: ApplicationData
    ): Promise<boolean> => {
      setState((prev) => ({ ...prev, loading: true, error: null }));

      try {
        await examApplicationService.applyToTarget(level, targetId, {
          examId,
          ...data,
        });

        // Reload exams after applying
        const response = await examApplicationService.getForTarget(level, targetId);
        setState((prev) => ({
          ...prev,
          applied: response.applied,
          available: response.available,
          loading: false,
        }));

        return true;
      } catch (err: unknown) {
        const error = err as { message?: string };
        setState((prev) => ({
          ...prev,
          loading: false,
          error: error.message ?? 'Erro ao aplicar exame',
        }));
        return false;
      }
    },
    []
  );

  /**
   * Apply multiple exams
   */
  const applyMultipleExams = useCallback(
    async (
      level: ApplicationLevel,
      targetId: number,
      examIds: number[],
      data?: ApplicationData
    ): Promise<boolean> => {
      setState((prev) => ({ ...prev, loading: true, error: null }));

      try {
        await examApplicationService.applyMultiple(level, targetId, examIds, data);

        // Reload exams after applying
        const response = await examApplicationService.getForTarget(level, targetId);
        setState((prev) => ({
          ...prev,
          applied: response.applied,
          available: response.available,
          loading: false,
        }));

        return true;
      } catch (err: unknown) {
        const error = err as { message?: string };
        setState((prev) => ({
          ...prev,
          loading: false,
          error: error.message ?? 'Erro ao aplicar exames',
        }));
        return false;
      }
    },
    []
  );

  /**
   * Remove a single exam
   */
  const removeExam = useCallback(
    async (
      level: ApplicationLevel,
      targetId: number,
      examId: number
    ): Promise<boolean> => {
      setState((prev) => ({ ...prev, loading: true, error: null }));

      try {
        await examApplicationService.removeFromTarget(level, targetId, examId);

        // Reload exams after removing
        const response = await examApplicationService.getForTarget(level, targetId);
        setState((prev) => ({
          ...prev,
          applied: response.applied,
          available: response.available,
          loading: false,
        }));

        return true;
      } catch (err: unknown) {
        const error = err as { message?: string };
        setState((prev) => ({
          ...prev,
          loading: false,
          error: error.message ?? 'Erro ao remover exame',
        }));
        return false;
      }
    },
    []
  );

  /**
   * Remove multiple exams
   */
  const removeMultipleExams = useCallback(
    async (
      level: ApplicationLevel,
      targetId: number,
      examIds: number[]
    ): Promise<boolean> => {
      setState((prev) => ({ ...prev, loading: true, error: null }));

      try {
        await examApplicationService.removeMultiple(level, targetId, examIds);

        // Reload exams after removing
        const response = await examApplicationService.getForTarget(level, targetId);
        setState((prev) => ({
          ...prev,
          applied: response.applied,
          available: response.available,
          loading: false,
        }));

        return true;
      } catch (err: unknown) {
        const error = err as { message?: string };
        setState((prev) => ({
          ...prev,
          loading: false,
          error: error.message ?? 'Erro ao remover exames',
        }));
        return false;
      }
    },
    []
  );

  /**
   * Update an application
   */
  const updateApplication = useCallback(
    async (
      level: ApplicationLevel,
      targetId: number,
      examId: number,
      data: ApplicationData
    ): Promise<boolean> => {
      setState((prev) => ({ ...prev, loading: true, error: null }));

      try {
        await examApplicationService.updateForTarget(level, targetId, examId, data);

        // Reload exams after updating
        const response = await examApplicationService.getForTarget(level, targetId);
        setState((prev) => ({
          ...prev,
          applied: response.applied,
          available: response.available,
          loading: false,
        }));

        return true;
      } catch (err: unknown) {
        const error = err as { message?: string };
        setState((prev) => ({
          ...prev,
          loading: false,
          error: error.message ?? 'Erro ao atualizar aplicação',
        }));
        return false;
      }
    },
    []
  );

  /**
   * Clear main error
   */
  const clearError = useCallback(() => {
    setState((prev) => ({ ...prev, error: null }));
  }, []);

  /**
   * Clear alerts error
   */
  const clearAlertsError = useCallback(() => {
    setState((prev) => ({ ...prev, alertsError: null }));
  }, []);

  return {
    ...state,
    loadExams,
    loadAlerts,
    applyExam,
    applyMultipleExams,
    removeExam,
    removeMultipleExams,
    updateApplication,
    clearError,
    clearAlertsError,
  };
}

export default useExamApplications;
